import {
  DraftMessageId,
  MessageId,
} from "@redotech/effect-api-shared/ids/definitions";
import { useRequiredContext } from "@redotech/react-util/context";
import { EffectApiClientContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Effect } from "effect";
import { memo, useCallback, useEffect, useState } from "react";

// Hardcoded test IDs - replace with real IDs from your database
const TEST_MESSAGE_ID = MessageId.fromBigInt(182_787_783_384_123_711n);
const TEST_DRAFT_MESSAGE_ID =
  DraftMessageId.fromBigInt(8_702_162_831_419_313_070n);

interface Attachment {
  readonly id: string;
  readonly url: string;
  readonly description: { _tag: "Some"; value: string } | { _tag: "None" };
  readonly mimeType: { _tag: "Some"; value: string } | { _tag: "None" };
}

export const AttachmentTest = memo(function AttachmentTest() {
  const effectApi = useRequiredContext(EffectApiClientContext);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [draftAttachments, setDraftAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const loadAttachments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        client.attachment
          .getMessageAttachments({ path: { messageId: TEST_MESSAGE_ID } })
          .pipe(Effect.orDie),
      );
      setAttachments([...result.attachments]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load attachments",
      );
      console.error("Error loading attachments:", err);
    } finally {
      setLoading(false);
    }
  }, [effectApi]);

  const loadDraftAttachments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = effectApi.getClient();
      const result = await effectApi.runPromise(
        client.attachment
          .getDraftMessageAttachments({
            path: { draftMessageId: TEST_DRAFT_MESSAGE_ID },
          })
          .pipe(Effect.orDie),
      );
      setDraftAttachments([...result.attachments]);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load draft attachments",
      );
      console.error("Error loading draft attachments:", err);
    } finally {
      setLoading(false);
    }
  }, [effectApi]);

  const loadAllAttachments = useCallback(async () => {
    await Promise.all([loadAttachments(), loadDraftAttachments()]);
  }, [loadAttachments, loadDraftAttachments]);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        setError(null);
      }
    },
    [],
  );

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setError("Please select a file first");
      return;
    }

    setUploading(true);
    setError(null);
    try {
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = reader.result as string;
          const base64Data = base64.split(",")[1];
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          resolve(base64Data);
        };
        reader.onerror = reject;
      });

      reader.readAsDataURL(selectedFile);
      const base64Data = await base64Promise;

      const client = effectApi.getClient();
      await effectApi.runPromise(
        client.attachment
          .addDraftAttachment({
            payload: {
              draftMessageId: TEST_DRAFT_MESSAGE_ID,
              file: base64Data,
              filename: selectedFile.name,
              mimeType: selectedFile.type,
              size: selectedFile.size,
            },
          })
          .pipe(Effect.orDie),
      );

      setSelectedFile(null);
      const fileInput = document.getElementById(
        "file-upload",
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }

      await loadAllAttachments();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to upload attachment",
      );
      console.error("Error uploading attachment:", err);
    } finally {
      setUploading(false);
    }
  }, [selectedFile, effectApi, loadAllAttachments]);

  useEffect(() => {
    void loadAllAttachments();
  }, [loadAllAttachments]);

  const getOptionValue = <T,>(
    option: { _tag: "Some"; value: T } | { _tag: "None" },
  ): T | null => {
    return option._tag === "Some" ? option.value : null;
  };

  return (
    <Flex dir="column" gap="lg" p="xl">
      <Text fontSize="xl" fontWeight="bold">
        Draft Attachment Test Component
      </Text>

      <Flex dir="column" gap="sm">
        <Text fontSize="sm" textColor="secondary">
          Testing messageId: {TEST_MESSAGE_ID}
        </Text>
        <Text fontSize="sm" textColor="secondary">
          Testing draftMessageId: {TEST_DRAFT_MESSAGE_ID}
        </Text>

        <RedoButton
          hierarchy="secondary"
          onClick={loadAllAttachments}
          size="sm"
          text="Refresh Attachments"
        />
      </Flex>

      <Flex
        bgColor="secondary"
        borderColor="primary"
        borderWidth="1px"
        dir="column"
        gap="md"
        p="md"
      >
        <Text fontSize="md" fontWeight="semibold">
          Upload New Draft Attachment
        </Text>

        <Flex dir="column" gap="sm">
          <input
            accept="*/*"
            id="file-upload"
            onChange={handleFileSelect}
            type="file"
          />

          {selectedFile && (
            <Text fontSize="sm" textColor="secondary">
              Selected: {selectedFile.name} (
              {(selectedFile.size / 1024).toFixed(2)} KB)
            </Text>
          )}

          <Flex gap="sm">
            <RedoButton
              disabled={!selectedFile || uploading}
              hierarchy="primary"
              onClick={handleUpload}
              size="sm"
              text={uploading ? "Uploading..." : "Upload Attachment"}
            />
            {selectedFile && !uploading && (
              <RedoButton
                hierarchy="secondary"
                onClick={() => {
                  setSelectedFile(null);
                  const fileInput = document.getElementById(
                    "file-upload",
                  ) as HTMLInputElement;
                  if (fileInput) {
                    fileInput.value = "";
                  }
                }}
                size="sm"
                text="Clear"
              />
            )}
          </Flex>
        </Flex>
      </Flex>

      {error && (
        <Flex bgColor="secondary" borderColor="error" borderWidth="1px" p="md">
          <Text fontSize="sm" textColor="error">
            Error: {error}
          </Text>
        </Flex>
      )}

      {loading && (
        <Text fontSize="sm" textColor="secondary">
          Loading attachments...
        </Text>
      )}

      {!loading && attachments.length === 0 && (
        <Text fontSize="sm" textColor="secondary">
          No attachments found for this message
        </Text>
      )}

      {!loading && attachments.length > 0 && (
        <Flex dir="column" gap="md">
          <Text fontSize="md" fontWeight="semibold">
            Message Attachments ({attachments.length})
          </Text>

          {attachments.map((attachment) => {
            const description = getOptionValue(attachment.description);
            const mimeType = getOptionValue(attachment.mimeType);
            const isImage = mimeType?.startsWith("image/");

            return (
              <Flex
                bgColor="secondary"
                borderColor="primary"
                borderWidth="1px"
                dir="column"
                gap="sm"
                key={attachment.id}
                p="md"
              >
                <Flex align="center" gap="md" justify="space-between">
                  <Flex dir="column" gap="xs">
                    <Text fontSize="sm" fontWeight="semibold">
                      {description || "Unnamed attachment"}
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      Type: {mimeType || "unknown"}
                    </Text>
                  </Flex>

                  <RedoButton
                    hierarchy="secondary"
                    onClick={() => window.open(attachment.url, "_blank")}
                    size="sm"
                    text="Open"
                  />
                </Flex>

                {isImage && (
                  <img
                    alt={description || "Attachment"}
                    src={attachment.url}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      )}

      {!loading && draftAttachments.length === 0 && (
        <Text fontSize="sm" textColor="secondary">
          No attachments found for this draft message
        </Text>
      )}

      {!loading && draftAttachments.length > 0 && (
        <Flex dir="column" gap="md">
          <Text fontSize="md" fontWeight="semibold">
            Draft Message Attachments ({draftAttachments.length})
          </Text>

          {draftAttachments.map((attachment) => {
            const description = getOptionValue(attachment.description);
            const mimeType = getOptionValue(attachment.mimeType);
            const isImage = mimeType?.startsWith("image/");

            return (
              <Flex
                bgColor="secondary"
                borderColor="primary"
                borderWidth="1px"
                dir="column"
                gap="sm"
                key={attachment.id}
                p="md"
              >
                <Flex align="center" gap="md" justify="space-between">
                  <Flex dir="column" gap="xs">
                    <Text fontSize="sm" fontWeight="semibold">
                      {description || "Unnamed attachment"}
                    </Text>
                    <Text fontSize="xs" textColor="secondary">
                      Type: {mimeType || "unknown"}
                    </Text>
                  </Flex>

                  <RedoButton
                    hierarchy="secondary"
                    onClick={() => window.open(attachment.url, "_blank")}
                    size="sm"
                    text="Open"
                  />
                </Flex>

                {isImage && (
                  <img
                    alt={description || "Attachment"}
                    src={attachment.url}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "300px",
                      objectFit: "contain",
                    }}
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
});
